/**
 * pages/procurement/BudgetDetailPage.jsx — budget workspace.
 *
 * Scenario tabs · property/state/commodity filters · stacked supply/delivery/
 * tax/added projection chart · totals · by-scope table · budget-vs-actual.
 */
import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import {
  ResponsiveContainer, ComposedChart, LineChart, Bar, Line, XAxis, YAxis, Tooltip, Legend, CartesianGrid,
} from 'recharts';
import {
  ArrowLeft, Plus, Pencil, Trash2, RefreshCw, Loader2, TrendingUp, Sparkles, Pin, PinOff, FileSignature, ChevronDown, FileText, Building2, Info, History, LineChart as LineChartIcon, MoreVertical,
} from 'lucide-react';

import MainLayout from '../../components/MainLayout';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from '../../components/ui/select';
import {
  Tooltip as UITooltip, TooltipTrigger, TooltipContent, TooltipProvider,
} from '../../components/ui/tooltip';
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator,
} from '../../components/ui/dropdown-menu';
import { ScenarioEditor } from '../../components/procurement/ScenarioEditor';
import { RateModelingDialog } from '../../components/procurement/RateModelingDialog';
import { AIPromptDialog } from '../../components/procurement/AIPromptDialog';
import { ProjectedPropertyDialog } from '../../components/procurement/ProjectedPropertyDialog';
import { BudgetBuildProgress } from '../../components/procurement/BudgetBuildProgress';
import { ScenarioHistoryDialog } from '../../components/procurement/ScenarioHistoryDialog';
import { YoyEscalationDialog } from '../../components/procurement/YoyEscalationDialog';
import { CommodityChart } from '../../components/procurement/CommodityChart';
import { ScenarioConfigPanel } from '../../components/procurement/ScenarioConfigPanel';
import { RateCompareChart } from '../../components/procurement/RateCompareChart';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../../components/ui/dialog';
import { BudgetCopilotPanel } from '../../components/procurement/BudgetCopilotPanel';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../../components/ui/tabs';
import {
  Accordion, AccordionItem, AccordionTrigger, AccordionContent,
} from '../../components/ui/accordion';
import {
  getBudget, getBaseline, getProjection, getBudgetVsActual, getRateSources,
  deleteScenario, patchScenario, createScenarioFromSource, rebuildBaseline,
  getProjectedSources, deleteProjectedProperty, getMarketOverlay,
  createMarketSeasonalScenario, HORIZONS, downloadMethodology, patchBudget,
} from '../../api/budgets';
import { getCurrentAccount } from '../../lib/auth';

const ALL = '__all__';
const money = (n) => (n == null ? '—' : `$${Number(n).toLocaleString(undefined, { maximumFractionDigits: 0 })}`);
const horizonLabel = (m) => HORIZONS.find((h) => h.value === m)?.label || `${m} mo`;

const COLORS = { supply: '#0ea5e9', delivery: '#6366f1', tax: '#f59e0b', added: '#94a3b8' };

// Granular water/sewer component bars (Chunk 2) — shown for water/sewer budgets.
const WATER_COMP = [
  { key: 'water_supply', name: 'Water Supply', color: '#0ea5e9' },
  { key: 'water_usage', name: 'Water Usage', color: '#38bdf8' },
  { key: 'sewer_access', name: 'Sewer Access', color: '#6366f1' },
  { key: 'sewer_usage', name: 'Sewer Usage', color: '#818cf8' },
  { key: 'stormwater', name: 'Stormwater', color: '#14b8a6' },
  { key: 'tax_fees', name: 'Tax & fees', color: '#f59e0b' },
];

const Kpi = ({ label, value, sub, accent }) => (
  <div className="border rounded-md p-4 bg-card" data-testid={`budget-kpi-${label.toLowerCase().replace(/\s+/g, '-')}`}>
    <div className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">{label}</div>
    <div className={`text-2xl font-bold mt-1 tracking-tight ${accent || ''}`}>{value}</div>
    {sub && <div className="text-xs text-muted-foreground mt-0.5">{sub}</div>}
  </div>
);

export default function BudgetDetailPage() {
  const { budgetId } = useParams();
  const navigate = useNavigate();
  const account = getCurrentAccount();
  const accountId = account?.id;

  const [budget, setBudget] = useState(null);
  const [baseline, setBaseline] = useState(null);
  const [scenarioId, setScenarioId] = useState(null);
  const [projection, setProjection] = useState(null);
  const [bva, setBva] = useState(null);
  const [filters, setFilters] = useState({ property: ALL, state: ALL, commodity: ALL, meter: ALL });
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [projLoading, setProjLoading] = useState(true);
  const [editorOpen, setEditorOpen] = useState(false);
  const [editingScenario, setEditingScenario] = useState(null);
  const [rateDialogOpen, setRateDialogOpen] = useState(false);
  const [aiDialogOpen, setAiDialogOpen] = useState(false);
  const [rateSources, setRateSources] = useState({ contracts: [], market_feeds: [] });
  const [projDialogOpen, setProjDialogOpen] = useState(false);
  const [projSources, setProjSources] = useState([]);
  const [marketData, setMarketData] = useState(null);
  const [chartMode, setChartMode] = useState('cost'); // 'cost' | 'price'
  const [expandedChart, setExpandedChart] = useState(null);
  const [viewRange, setViewRange] = useState('12'); // months shown in the chart: '12'|'24'|'36'|'all'
  const [historyScenario, setHistoryScenario] = useState(null);
  const [yoyOpen, setYoyOpen] = useState(false);
  const [beaconOpen, setBeaconOpen] = useState(false);

  const loadBudget = useCallback(async () => {
    if (!accountId) return;
    setLoading(true);
    try {
      const [b, bl, rs, ps] = await Promise.all([
        getBudget({ accountId, id: budgetId }),
        getBaseline({ accountId, id: budgetId }).catch(() => null),
        getRateSources({ accountId }).catch(() => null),
        getProjectedSources({ accountId, id: budgetId }).catch(() => null),
      ]);
      setBudget(b);
      setBaseline(bl);
      if (rs) setRateSources(rs);
      if (ps) setProjSources(ps.sources || []);
      setScenarioId((prev) => prev || b.base_scenario_id || b.scenarios?.[0]?.id);
    } catch (e) {
      toast.error('Failed to load budget', { description: e?.response?.data?.detail || e.message });
    } finally {
      setLoading(false);
    }
  }, [accountId, budgetId]);

  useEffect(() => { loadBudget(); }, [loadBudget]);

  const removeProjected = async (proj) => {
    if (!window.confirm(`Remove projected property "${proj.name}"?`)) return;
    try {
      await deleteProjectedProperty({ accountId, id: budgetId, projId: proj.id });
      toast.success('Projected property removed');
      await loadBudget();
      await loadProjection();
    } catch (e) {
      toast.error('Could not remove', { description: e?.response?.data?.detail || e.message });
    }
  };

  const togglePin = async (sc) => {
    try {
      await patchScenario({ accountId, id: budgetId, scenarioId: sc.id, patch: { pinned: !sc.pinned } });
      loadBudget();
    } catch (e) {
      toast.error('Could not update pin', { description: e?.response?.data?.detail || e.message });
    }
  };

  const createFromSource = async (sourceType, sourceId) => {
    try {
      const sc = await createScenarioFromSource({ accountId, id: budgetId, sourceType, sourceId });
      toast.success('Scenario created', { description: sc.name });
      setScenarioId(sc.id);
      loadBudget();
    } catch (e) {
      toast.error('Could not create scenario', { description: e?.response?.data?.detail || e.message });
    }
  };

  const handleMarketSeasonal = async () => {
    try {
      const sc = await createMarketSeasonalScenario({ accountId, id: budgetId });
      toast.success('Market-seasonal scenario created', { description: sc.name });
      setScenarioId(sc.id);
      loadBudget();
    } catch (e) {
      toast.error('Could not model from market', { description: e?.response?.data?.detail || e.message });
    }
  };

  const loadProjection = useCallback(async () => {
    if (!accountId || !scenarioId) { setProjLoading(false); return; }
    setProjLoading(true);
    const params = {
      accountId, id: budgetId, scenarioId,
      propertyKey: filters.property !== ALL ? filters.property : undefined,
      state: filters.state !== ALL ? filters.state : undefined,
      commodity: filters.commodity !== ALL ? filters.commodity : undefined,
      meterNumber: filters.meter !== ALL ? filters.meter : undefined,
    };
    try {
      const [proj, va, mkt] = await Promise.all([
        getProjection(params),
        getBudgetVsActual(params).catch(() => null),
        getMarketOverlay(params).catch(() => null),
      ]);
      setProjection(proj);
      setBva(va);
      setMarketData(mkt);
    } catch (e) {
      toast.error('Failed to compute projection', { description: e?.response?.data?.detail || e.message });
    } finally {
      setProjLoading(false);
    }
  }, [accountId, budgetId, scenarioId, filters]);

  useEffect(() => { loadProjection(); }, [loadProjection]);

  const handleRebuild = async () => {
    setBusy(true);
    try {
      await rebuildBaseline({ accountId, id: budgetId });
      toast.success('Baseline rebuilt from latest bills');
      await loadBudget();
      await loadProjection();
    } catch (e) {
      toast.error('Rebuild failed', { description: e?.response?.data?.detail || e.message });
    } finally { setBusy(false); }
  };

  const toggleForecastMethod = async () => {
    const next = (budget?.forecast_method === 'actuals_yoy') ? 'legacy' : 'actuals_yoy';
    setBusy(true);
    try {
      await patchBudget({ accountId, id: budgetId, patch: { forecast_method: next } });
      toast.success(next === 'actuals_yoy'
        ? 'Actuals-anchored forecast on — past locks to actuals, future uses YoY'
        : 'Reverted to scenario projection');
      await loadBudget();
      await loadProjection();
    } catch (e) {
      toast.error('Could not change forecast mode', { description: e?.response?.data?.detail || e.message });
    } finally { setBusy(false); }
  };

  const handleDeleteScenario = async (sc) => {
    if (sc.is_base) { toast.error('The base scenario cannot be deleted'); return; }
    if (!window.confirm(`Delete scenario "${sc.name}"?`)) return;
    try {
      await deleteScenario({ accountId, id: budgetId, scenarioId: sc.id });
      toast.success('Scenario deleted');
      if (scenarioId === sc.id) setScenarioId(budget.base_scenario_id);
      loadBudget();
    } catch (e) {
      toast.error('Delete failed', { description: e?.response?.data?.detail || e.message });
    }
  };

  const openNewScenario = () => { setEditingScenario(null); setEditorOpen(true); };
  const openEditScenario = (sc) => { setEditingScenario(sc); setEditorOpen(true); };

  if (loading) {
    return <MainLayout><div className="flex items-center justify-center h-64 text-muted-foreground"><Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading budget…</div></MainLayout>;
  }
  if (!budget) return <MainLayout><div className="p-8">Budget not found.</div></MainLayout>;

  // Still materialising the baseline → show the guided creation journey.
  if (budget.build_status && budget.build_status !== 'ready') {
    return (
      <MainLayout>
        <BudgetBuildProgress
          accountId={accountId}
          budgetId={budgetId}
          name={budget.name}
          onReady={() => { loadBudget(); loadProjection(); }}
        />
      </MainLayout>
    );
  }

  const f = baseline?.filters || { states: [], commodities: [], properties: [] };
  // Which modelling tools are relevant for this budget's commodities (#3, #5).
  const blCommodities = f.commodities || [];
  const marketRelevant = blCommodities.some((c) => c === 'electricity' || c === 'gas');
  const hasWater = blCommodities.some((c) => c === 'water' || c === 'sewer');
  const isWaterBudget = hasWater && !marketRelevant;
  const marketByMonth = {};
  (marketData?.months || []).forEach((m, i) => {
    marketByMonth[m] = {
      market: marketData.series.market[i],
      actual: marketData.series.actual[i],
    };
  });
  const chartData = (projection?.by_month || []).map((m) => ({
    month: m.month.slice(2),
    monthFull: m.month,
    supply: m.supply, delivery: m.delivery, tax: m.tax, added: m.added,
    water_supply: m.water_supply, water_usage: m.water_usage,
    sewer_access: m.sewer_access, sewer_usage: m.sewer_usage,
    stormwater: m.stormwater, tax_fees: m.tax_fees,
    budget: m.total,
    market: marketByMonth[m.month]?.market ?? null,
    actual: marketByMonth[m.month]?.actual ?? null,
  }));
  // Date-range window: show N months of the horizon (default 12). In actuals_yoy
  // mode the chart starts at the first complete-portfolio month (the sparse
  // ramp before it is intentionally blanked, so we don't open on empty months).
  const allMonths = (projection?.by_month || []).map((m) => m.month);
  const fcMeta = marketData?.meta?.forecast || null;
  const startIdx = fcMeta?.auto_start_month
    ? Math.max(0, allMonths.indexOf(fcMeta.auto_start_month)) : 0;
  const windowCount = viewRange === 'all' ? allMonths.length : Number(viewRange);
  const windowMonths = new Set(allMonths.slice(startIdx, startIdx + windowCount));
  const visibleChartData = chartData.filter((d) => windowMonths.has(d.monthFull));
  const pricedCommodities = marketData?.meta?.commodities_priced || [];
  const hasMarket = pricedCommodities.length > 0;

  // Per-commodity (de-blended) charts — electricity & gas are distinct commodities
  // with their own usage, rates and markets, so we render one chart each.
  const commodityMonth = projection?.by_commodity_month || {};
  const commodityKeys = Object.keys(commodityMonth);
  const isMultiCommodity = commodityKeys.length >= 2;
  const ELEC_GAS_BARS = [
    { key: 'supply', name: 'Supply', color: COLORS.supply },
    { key: 'delivery', name: 'Delivery', color: COLORS.delivery },
    { key: 'tax', name: 'Tax', color: COLORS.tax },
    { key: 'added', name: 'Other & fees', color: COLORS.added },
  ];
  const commodityCharts = commodityKeys.map((comm) => {
    const mc = marketData?.by_commodity?.[comm];
    const feed = (marketData?.meta?.feeds_used || []).find((fd) => fd.commodity === comm);
    const isWaterComm = comm === 'water' || comm === 'sewer';
    let data;
    if (fcMeta && mc && Array.isArray(mc.supply)) {
      // actuals_yoy: stacked bars (actual splits for the past, forecast splits
      // for the future) AND the actual/market lines all come from one series,
      // so the bars reconcile to the actual line exactly.
      const mm = marketData.months || [];
      data = mm.map((mo, i) => ({
        month: mo.slice(2), monthFull: mo,
        supply: mc.supply?.[i] ?? null,
        delivery: mc.delivery?.[i] ?? null,
        tax: mc.tax?.[i] ?? null,
        added: mc.added?.[i] ?? null,
        budget: mc.budget?.[i] ?? null,
        market: mc.market?.[i] ?? null,
        actual: mc.actual?.[i] ?? null,
      })).filter((d) => windowMonths.has(d.monthFull));
    } else {
      const rows = commodityMonth[comm].rows || [];
      data = rows.map((r, i) => ({
        month: r.month.slice(2), monthFull: r.month,
        supply: r.supply, delivery: r.delivery, tax: r.tax, added: r.added,
        water_supply: r.water_supply, water_usage: r.water_usage,
        sewer_access: r.sewer_access, sewer_usage: r.sewer_usage,
        stormwater: r.stormwater, tax_fees: r.tax_fees,
        budget: r.total,
        market: mc?.market ? mc.market[i] : null,
        actual: mc?.actual ? mc.actual[i] : null,
      })).filter((d) => windowMonths.has(d.monthFull));
    }
    return {
      comm,
      label: { electricity: 'Electricity', gas: 'Gas', water: 'Water', sewer: 'Sewer', trash: 'Trash' }[comm] || comm,
      data,
      bars: isWaterComm ? WATER_COMP : ELEC_GAS_BARS,
      hasMarket: !!(mc && mc.market),
      feedLabel: feed ? `${feed.feed}` : null,
      totals: commodityMonth[comm].totals || {},
    };
  });
  const priceData = (() => {
    if (!hasMarket) return [];
    const byMonth = {};
    Object.entries(marketData.raw_price || {}).forEach(([comm, info]) => {
      (info.points || []).forEach((p) => {
        if (!windowMonths.has(p.month)) return;
        byMonth[p.month] = byMonth[p.month] || { month: p.month.slice(2) };
        byMonth[p.month][comm] = p.rate;
      });
    });
    return Object.keys(byMonth).sort().map((k) => byMonth[k]);
  })();
  const t = projection?.totals || {};
  const activeScenario = (budget.scenarios || []).find((s) => s.id === scenarioId);
  // Explain to the user exactly what the budget number is priced on (#user ask).
  const scnSource = activeScenario?.assumptions?.source || 'manual';
  const scnHasOverrides = (activeScenario?.assumptions?.rate_overrides || []).length > 0;
  const budgetBasisLine = isWaterBudget
    ? "each property's own historical water & sewer charges (trailing ~12 months of bills), split into components (water supply/usage, sewer access/usage, stormwater, tax & fees) and grown by this scenario's per-component % increases."
    : scnSource === 'contract'
      ? "your historical usage (trailing ~12 months) with the supply portion priced at this scenario's contracted rates; delivery & tax stay at historical rates, escalated by the budget %."
      : (scnSource === 'market' || scnSource === 'market-seasonal')
        ? "your historical usage (trailing ~12 months) with the supply portion re-priced at the live market hub rate (electricity ISO DA-LMP / gas Henry Hub); delivery & tax stay at historical rates."
        : scnHasOverrides
          ? "your historical usage (trailing ~12 months) priced at this scenario's overridden supply/delivery $/unit rates."
          : "your historical usage (trailing ~12 months) priced at each property's own historical bill rate, escalated by the budget's %/yr and shaped by its seasonal pattern.";
  const CalcBasisInfo = (
    <TooltipProvider delayDuration={150}>
      <UITooltip>
        <TooltipTrigger asChild>
          <button type="button" className="text-muted-foreground hover:text-foreground" data-testid="bva-basis-info" aria-label="How is this calculated?">
            <Info className="w-3.5 h-3.5" />
          </button>
        </TooltipTrigger>
        <TooltipContent side="bottom" align="start" className="max-w-sm text-xs leading-relaxed">
          <p className="font-semibold mb-1">How this is calculated</p>
          <p className="mb-1.5"><span className="font-medium">Budget</span> = {budgetBasisLine}</p>
          <p><span className="font-medium">Actual</span> = the sum of posted bills (current bill amount) for the months that have already elapsed.</p>
          <p className="mt-1.5 text-muted-foreground">Switch scenarios to change the pricing basis (historical, contracted or market rates).</p>
        </TooltipContent>
      </UITooltip>
    </TooltipProvider>
  );
  // Property-first meter drill-down: meters belonging to the selected property
  const meterOptions = (budget.granularity === 'meter' && filters.property !== ALL)
    ? Array.from(new Set((baseline?.scopes || [])
        .filter((s) => s.property_key === filters.property && s.meter_number)
        .map((s) => s.meter_number)))
    : [];

  return (
    <MainLayout>
      <div className="space-y-6">
        {/* Header */}
        <div>
          <button onClick={() => navigate('/procurement/budgets')} className="text-sm text-muted-foreground hover:text-foreground flex items-center gap-1 mb-2" data-testid="budget-back">
            <ArrowLeft className="w-4 h-4" /> Back to Budgets
          </button>
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div>
              <h1 className="text-2xl font-bold tracking-tight" data-testid="budget-title">{budget.name}</h1>
              <div className="flex items-center gap-2 mt-1">
                <Badge variant="outline">{horizonLabel(budget.horizon_months)}</Badge>
                <Badge variant="outline">Start {budget.start_month}</Badge>
                <Badge variant="outline" className="capitalize">{(budget.commodities || []).length ? budget.commodities.join(' · ') : 'All commodities'}</Badge>
                <span className="text-xs text-muted-foreground">Escalation {budget.escalation_pct}%/yr</span>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Button variant="outline" onClick={() => setBeaconOpen(true)} data-testid="budget-beacon-btn">
                <Sparkles className="w-4 h-4 mr-1.5 text-violet-600" /> Ask Beacon
              </Button>
              <Button variant="outline" onClick={handleRebuild} disabled={busy} data-testid="budget-rebuild-btn">
                {busy ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-1.5" />} Rebuild baseline
              </Button>
              <Button variant="outline" onClick={() => downloadMethodology({ accountId, id: budgetId, budgetName: budget.name })
                .catch((e) => toast.error('Download failed', { description: e?.response?.data?.detail || e.message }))}
                data-testid="download-methodology-btn">
                <FileText className="w-4 h-4 mr-1.5" /> Download logic
              </Button>
              <Button onClick={() => {
                const qp = new URLSearchParams();
                if (scenarioId) qp.set('scenario', scenarioId);
                if (filters.property !== ALL) qp.set('property', filters.property);
                if (filters.state !== ALL) qp.set('state', filters.state);
                if (filters.commodity !== ALL) qp.set('commodity', filters.commodity);
                if (filters.meter !== ALL) qp.set('meter', filters.meter);
                navigate(`/procurement/budgets/${budgetId}/report?${qp.toString()}`);
              }} data-testid="generate-report-btn">
                <FileText className="w-4 h-4 mr-1.5" /> Generate Report
              </Button>
            </div>
          </div>
        </div>

        {/* KPIs */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <Kpi label="Baseline (annual)" value={money(budget.baseline_summary?.annual_total)} sub={`${budget.baseline_summary?.scope_count ?? 0} scopes`} />
          <Kpi label="Projected Total" value={money(t.total)} sub={`over ${budget.horizon_months} months`} accent="text-sky-600" />
          {isMultiCommodity ? (
            commodityCharts.slice(0, 2).map((c) => (
              <Kpi key={c.comm} label={c.label} value={money(c.totals.total)}
                sub={t.total ? `${Math.round(((c.totals.total || 0) / t.total) * 100)}% of spend` : ''}
                accent={c.comm === 'electricity' ? 'text-amber-600' : c.comm === 'gas' ? 'text-orange-600' : 'text-sky-600'} />
            ))
          ) : (
            <>
              <Kpi label="Supply" value={money(t.supply)} sub={t.total ? `${Math.round((t.supply / t.total) * 100)}% of spend` : ''} />
              <Kpi label="Delivery" value={money(t.delivery)} sub={t.total ? `${Math.round((t.delivery / t.total) * 100)}% of spend` : ''} />
            </>
          )}
        </div>
        {isMultiCommodity && (
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-2 -mt-1" data-testid="commodity-stat-split">
            {commodityCharts.map((c) => (
              <div key={c.comm} className="border rounded-md p-2.5 bg-card/60" data-testid={`commodity-stat-${c.comm}`}>
                <div className="text-xs font-semibold capitalize mb-1">{c.label} breakdown</div>
                <div className="grid grid-cols-2 gap-x-3 gap-y-0.5 text-[11px] text-muted-foreground">
                  <span>Supply</span><span className="text-right text-foreground">{money(c.totals.supply)}</span>
                  <span>Delivery</span><span className="text-right text-foreground">{money(c.totals.delivery)}</span>
                  <span>Tax</span><span className="text-right text-foreground">{money(c.totals.tax)}</span>
                  <span>Other &amp; fees</span><span className="text-right text-foreground">{money(c.totals.added)}</span>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Scenario tabs */}
        <div className="border-b">
          <div className="flex items-end gap-1 flex-wrap">
            {(budget.scenarios || []).map((sc) => {
              const active = scenarioId === sc.id;
              return (
                <div key={sc.id}
                  className={`group flex items-center rounded-t-md border border-b-0 pl-1 pr-0.5 -mb-px transition-colors ${
                    active ? 'border-border bg-card' : 'border-transparent hover:bg-muted/50'}`}>
                  <button onClick={() => setScenarioId(sc.id)}
                    className={`px-2.5 py-2 text-sm font-medium border-b-2 ${
                      active ? 'border-primary text-foreground' : 'border-transparent text-muted-foreground'}`}
                    data-testid={`scenario-tab-${sc.id}`}>
                    {sc.name}{sc.is_base && <span className="text-[10px] text-muted-foreground ml-1">(base)</span>}
                    {sc.pinned && <Pin className="w-3 h-3 inline ml-1 text-amber-500 fill-current" />}
                  </button>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <button
                        className="px-1 py-1 text-muted-foreground hover:text-foreground rounded opacity-0 group-hover:opacity-100 focus:opacity-100 data-[state=open]:opacity-100"
                        title="Scenario actions" data-testid={`scenario-menu-${sc.id}`}>
                        <MoreVertical className="w-4 h-4" />
                      </button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" className="w-44">
                      <DropdownMenuItem onClick={() => openEditScenario(sc)} data-testid={`scenario-edit-${sc.id}`}>
                        <Pencil className="w-3.5 h-3.5 mr-2" /> Edit
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => setHistoryScenario(sc)} data-testid={`scenario-history-${sc.id}`}>
                        <History className="w-3.5 h-3.5 mr-2" /> History / rollback
                      </DropdownMenuItem>
                      {!sc.is_base && (
                        <DropdownMenuItem onClick={() => togglePin(sc)} data-testid={`scenario-pin-${sc.id}`}>
                          {sc.pinned
                            ? <><PinOff className="w-3.5 h-3.5 mr-2" /> Unpin</>
                            : <><Pin className="w-3.5 h-3.5 mr-2" /> Pin to top</>}
                        </DropdownMenuItem>
                      )}
                      {!sc.is_base && (
                        <>
                          <DropdownMenuSeparator />
                          <DropdownMenuItem onClick={() => handleDeleteScenario(sc)}
                            className="text-destructive focus:text-destructive" data-testid={`scenario-del-${sc.id}`}>
                            <Trash2 className="w-3.5 h-3.5 mr-2" /> Delete
                          </DropdownMenuItem>
                        </>
                      )}
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
              );
            })}
            <div className="flex items-center gap-2 flex-wrap pb-2 pl-2">
              <Button size="sm" variant="outline" onClick={openNewScenario} data-testid="scenario-new-btn">
                <Plus className="w-3.5 h-3.5 mr-1" /> New Scenario
              </Button>
              <Button size="sm" onClick={() => setAiDialogOpen(true)} className="bg-violet-600 hover:bg-violet-700" data-testid="ai-model-btn">
                <Sparkles className="w-3.5 h-3.5 mr-1" /> Model with AI
              </Button>
              {activeScenario && (
                <Button size="sm" variant="outline" onClick={() => setRateDialogOpen(true)} data-testid="model-rates-btn">
                  <Pencil className="w-3.5 h-3.5 mr-1" /> Model rates
                </Button>
              )}
              <Button size="sm" variant="outline" onClick={() => setYoyOpen(true)} data-testid="yoy-btn">
                <TrendingUp className="w-3.5 h-3.5 mr-1 text-indigo-600" /> YoY %
              </Button>
              {marketRelevant && (
                <Button size="sm" variant="outline" onClick={handleMarketSeasonal} data-testid="market-seasonal-btn">
                  <LineChartIcon className="w-3.5 h-3.5 mr-1 text-emerald-600" /> Market (seasonal)
                </Button>
              )}
              {(rateSources.contracts.length > 0 || rateSources.market_feeds.length > 0) && (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                <Button size="sm" variant="outline" data-testid="from-source-btn">
                  <FileSignature className="w-3.5 h-3.5 mr-1" /> From contract / market <ChevronDown className="w-3 h-3 ml-1" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start" className="w-72 max-h-80 overflow-y-auto">
                {rateSources.contracts.filter((c) => c.rate != null).length > 0 && (
                  <>
                    <DropdownMenuLabel>Contracts</DropdownMenuLabel>
                    {rateSources.contracts.filter((c) => c.rate != null).map((c) => (
                      <DropdownMenuItem key={c.id} onClick={() => createFromSource('contract', c.id)} data-testid={`from-contract-${c.id}`}>
                        <span className="truncate">{c.name}</span>
                        <span className="ml-auto text-xs text-muted-foreground capitalize">{c.commodity} · {c.rate}</span>
                      </DropdownMenuItem>
                    ))}
                  </>
                )}
                {rateSources.market_feeds.length > 0 && (
                  <>
                    <DropdownMenuSeparator />
                    <DropdownMenuLabel>Market feeds</DropdownMenuLabel>
                    {rateSources.market_feeds.map((mf) => (
                      <DropdownMenuItem key={mf.symbol} onClick={() => createFromSource('market', mf.symbol)} data-testid={`from-market-${mf.symbol}`}>
                        <span className="truncate">{mf.name}</span>
                        <span className="ml-auto text-xs text-muted-foreground">{mf.latest_value}</span>
                      </DropdownMenuItem>
                    ))}
                  </>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
          )}
            </div>
          </div>
        </div>

        {/* Active scenario configuration — visible & editable assumptions + rates */}
        {activeScenario && (
          <ScenarioConfigPanel
            scenario={activeScenario}
            rateBasis={projection?.rate_basis}
            onEdit={openEditScenario}
          />
        )}

        {/* Filters */}
        <div className="flex items-center gap-2 flex-wrap">
          <Select value={filters.property} onValueChange={(v) => setFilters((s) => ({ ...s, property: v, meter: ALL }))}>
            <SelectTrigger className="w-56" data-testid="budget-filter-property"><SelectValue placeholder="Property" /></SelectTrigger>
            <SelectContent>
              <SelectItem value={ALL}>All properties</SelectItem>
              {f.properties.map((p, pi) => <SelectItem key={`${p.key}-${pi}`} value={p.key}>{p.name}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={filters.state} onValueChange={(v) => setFilters((s) => ({ ...s, state: v }))}>
            <SelectTrigger className="w-32" data-testid="budget-filter-state"><SelectValue placeholder="State" /></SelectTrigger>
            <SelectContent>
              <SelectItem value={ALL}>All states</SelectItem>
              {f.states.map((st) => <SelectItem key={st} value={st}>{st}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={filters.commodity} onValueChange={(v) => setFilters((s) => ({ ...s, commodity: v }))}>
            <SelectTrigger className="w-40" data-testid="budget-filter-commodity"><SelectValue placeholder="Commodity" /></SelectTrigger>
            <SelectContent>
              <SelectItem value={ALL}>All commodities</SelectItem>
              {f.commodities.map((c) => <SelectItem key={c} value={c} className="capitalize">{c}</SelectItem>)}
            </SelectContent>
          </Select>
          {/* Property-first meter drill-down: pick a property, then its meters */}
          {budget.granularity === 'meter' && filters.property === ALL && (
            <span className="text-xs text-muted-foreground italic" data-testid="meter-drilldown-hint">Pick a property to drill into meters</span>
          )}
          {budget.granularity === 'meter' && filters.property !== ALL && meterOptions.length > 0 && (
            <Select value={filters.meter} onValueChange={(v) => setFilters((s) => ({ ...s, meter: v }))}>
              <SelectTrigger className="w-48" data-testid="budget-filter-meter"><SelectValue placeholder="Meter" /></SelectTrigger>
              <SelectContent>
                <SelectItem value={ALL}>All meters in property</SelectItem>
                {meterOptions.map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}
              </SelectContent>
            </Select>
          )}
          <span className="text-xs text-muted-foreground ml-auto">{projLoading && !projection ? '…' : (projection?.scope_count ?? 0)} scopes in view</span>
        </div>

        {/* Projection chart — Budget breakdown + Actual + Market overlay */}
        <div className="border rounded-lg bg-card p-4" data-testid="projection-chart-card">
          <div className="flex items-center justify-between flex-wrap gap-2 mb-3">
            <div className="flex items-center gap-2">
              <TrendingUp className="w-4 h-4 text-sky-600" />
              <h2 className="text-sm font-semibold">Budget vs Actual vs Market</h2>
              {CalcBasisInfo}
              <button
                type="button"
                onClick={toggleForecastMethod}
                disabled={busy}
                data-testid="forecast-mode-toggle"
                title={budget?.forecast_method === 'actuals_yoy'
                  ? 'Actuals-anchored: past = actuals, chart starts at first full-portfolio month, future = YoY. Click to revert to scenario projection.'
                  : 'Scenario projection. Click to anchor the past to actuals and forecast the future with YoY.'}
                className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[11px] font-medium transition-colors ${
                  budget?.forecast_method === 'actuals_yoy'
                    ? 'border-sky-300 bg-sky-50 text-sky-700 hover:bg-sky-100'
                    : 'border-border text-muted-foreground hover:bg-muted'}`}>
                <span className={`h-1.5 w-1.5 rounded-full ${budget?.forecast_method === 'actuals_yoy' ? 'bg-sky-500' : 'bg-muted-foreground/40'}`} />
                Actuals-anchored {budget?.forecast_method === 'actuals_yoy' ? 'ON' : 'OFF'}
              </button>
              {activeScenario && <Badge variant="secondary" className="ml-1">{activeScenario.name}</Badge>}
              {(marketData?.meta?.feeds_used || []).map((fd) => (
                <Badge key={fd.symbol} variant="outline" className="capitalize">{fd.commodity}: {fd.feed}</Badge>
              ))}
            </div>
            <div className="flex items-center gap-2">
              <Select value={viewRange} onValueChange={setViewRange}>
                <SelectTrigger className="h-7 w-[120px] text-xs" data-testid="budget-range-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="12">First 12 mo</SelectItem>
                  <SelectItem value="24">First 24 mo</SelectItem>
                  <SelectItem value="36">First 36 mo</SelectItem>
                  <SelectItem value="all">Full horizon</SelectItem>
                </SelectContent>
              </Select>
              <div className="flex items-center gap-1 rounded-md border p-0.5">
                <Button size="sm" variant={chartMode === 'cost' ? 'secondary' : 'ghost'} className="h-7 text-xs"
                  onClick={() => setChartMode('cost')} data-testid="market-mode-cost">Spend ($)</Button>
                {marketRelevant && (
                  <Button size="sm" variant={chartMode === 'price' ? 'secondary' : 'ghost'} className="h-7 text-xs"
                    onClick={() => setChartMode('price')} disabled={!hasMarket} data-testid="market-mode-price">Market price</Button>
                )}
                {marketRelevant && (
                  <Button size="sm" variant={chartMode === 'rate' ? 'secondary' : 'ghost'} className="h-7 text-xs"
                    onClick={() => setChartMode('rate')} disabled={!hasMarket} data-testid="market-mode-rate">Rate vs market</Button>
                )}
              </div>
            </div>
          </div>
          {projLoading && !projection ? (
            <div className="h-64 flex flex-col items-center justify-center gap-3" data-testid="chart-loading">
              <div className="animate-spin h-7 w-7 rounded-full border-2 border-sky-500 border-t-transparent" />
              <span className="text-sm text-muted-foreground">Loading budget chart…</span>
            </div>
          ) : visibleChartData.length === 0 ? (
            <div className="h-64 flex items-center justify-center text-sm text-muted-foreground">No projection data.</div>
          ) : chartMode === 'price' ? (
            <ResponsiveContainer width="100%" height={320}>
              <LineChart data={priceData} margin={{ top: 8, right: 8, left: 8, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#eef2f7" vertical={false} />
                <XAxis dataKey="month" tick={{ fontSize: 11 }} />
                <YAxis tick={{ fontSize: 11 }} />
                <Tooltip />
                <Legend wrapperStyle={{ fontSize: 12 }} />
                {pricedCommodities.map((comm, i) => (
                  <Line key={comm} type="monotone" dataKey={comm} stroke={i === 0 ? '#10b981' : '#f59e0b'}
                    strokeWidth={2} dot={false} name={`${comm} (${marketData?.raw_price?.[comm]?.unit || ''})`} />
                ))}
              </LineChart>
            </ResponsiveContainer>
          ) : chartMode === 'rate' ? (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-3" data-testid="rate-compare-grid">
              {pricedCommodities.map((comm) => {
                const rc = marketData?.rate_compare?.[comm];
                if (!rc) return null;
                const allM = marketData.months || [];
                const idxs = allM.map((m, i) => (windowMonths.has(m) ? i : -1)).filter((i) => i >= 0);
                const feed = (marketData?.meta?.feeds_used || []).find((fd) => fd.commodity === comm);
                return (
                  <RateCompareChart key={comm} commodity={comm}
                    label={{ electricity: 'Electricity', gas: 'Gas', water: 'Water', sewer: 'Sewer', trash: 'Trash' }[comm] || comm}
                    months={idxs.map((i) => allM[i])}
                    billed={idxs.map((i) => rc.billed_supply_rate[i])}
                    market={idxs.map((i) => rc.market_rate[i])}
                    uom={rc.uom} avgBilled={rc.avg_billed} avgMarket={rc.avg_market}
                    position={rc.position} feedLabel={feed ? feed.feed : null} />
                );
              })}
            </div>
          ) : (
            isMultiCommodity ? (
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-3" data-testid="commodity-charts-grid">
                {commodityCharts.map((c) => (
                  <CommodityChart key={c.comm} commodity={c.comm} title={c.label}
                    data={c.data} bars={c.bars} hasMarket={c.hasMarket}
                    feedLabel={c.feedLabel} marketLabel="At market" height={300}
                    onExpand={() => setExpandedChart(c)} />
                ))}
              </div>
            ) : (
              commodityCharts.length === 1 ? (
                <CommodityChart commodity={commodityCharts[0].comm} title={commodityCharts[0].label}
                  data={commodityCharts[0].data} bars={commodityCharts[0].bars}
                  hasMarket={commodityCharts[0].hasMarket} feedLabel={commodityCharts[0].feedLabel}
                  marketLabel="At market" height={320}
                  onExpand={() => setExpandedChart(commodityCharts[0])} />
              ) : (
                <ResponsiveContainer width="100%" height={320}>
                  <ComposedChart data={visibleChartData} margin={{ top: 8, right: 8, left: 8, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#eef2f7" vertical={false} />
                    <XAxis dataKey="month" tick={{ fontSize: 11 }} />
                    <YAxis tick={{ fontSize: 11 }} tickFormatter={(v) => `$${(v / 1000).toFixed(0)}k`} />
                    <Tooltip formatter={(v) => (v == null ? '—' : money(v))} />
                    <Legend wrapperStyle={{ fontSize: 12 }} />
                    {isWaterBudget ? (
                      WATER_COMP.map((c) => (
                        <Bar key={c.key} dataKey={c.key} stackId="a" fill={c.color} name={c.name} />
                      ))
                    ) : (
                      <>
                        <Bar dataKey="supply" stackId="a" fill={COLORS.supply} name="Supply" />
                        <Bar dataKey="delivery" stackId="a" fill={COLORS.delivery} name="Delivery" />
                        <Bar dataKey="tax" stackId="a" fill={COLORS.tax} name="Tax" />
                        <Bar dataKey="added" stackId="a" fill={COLORS.added} name="Other & fees" />
                      </>
                    )}
                    {hasMarket && (
                      <Line type="monotone" dataKey="market" stroke="#10b981" strokeWidth={2.5}
                        strokeDasharray="5 4" dot={false} name="At market" connectNulls />
                    )}
                    <Line type="monotone" dataKey="actual" stroke="#ef4444" strokeWidth={2.5}
                      dot={{ r: 2 }} name="Actual" connectNulls />
                  </ComposedChart>
                </ResponsiveContainer>
              )
            )
          )}
          {projection?.rate_basis?.commodity_rates && Object.keys(projection.rate_basis.commodity_rates).length > 0 && chartMode === 'cost' && (
            <div className="flex flex-wrap items-center gap-x-3 gap-y-1 mt-2 text-[11px] text-muted-foreground" data-testid="chart-rate-basis">
              <span className="font-medium text-foreground">Calculated at:</span>
              {Object.entries(projection.rate_basis.commodity_rates).map(([comm, r]) => (
                <span key={comm} className="capitalize">
                  {comm} <span className="font-mono text-foreground">${Number(r.supply_per_unit).toFixed(4)}</span> supply
                  {' + '}<span className="font-mono text-foreground">${Number(r.delivery_per_unit).toFixed(4)}</span> delivery /{r.uom}
                </span>
              ))}
              {(projection.rate_basis.rate_overrides || []).length > 0 && (
                <Badge variant="outline" className="text-[10px]">{projection.rate_basis.rate_overrides.length} rate override(s) applied</Badge>
              )}
            </div>
          )}
          {hasMarket && chartMode === 'cost' && (
            <p className="text-[11px] text-muted-foreground mt-2 flex items-start gap-1.5">
              <Info className="w-3.5 h-3.5 mt-px shrink-0" /> {marketData?.meta?.note}
            </p>
          )}
        </div>

        {/* Results: Budget vs Actual + Forecast by property — tabbed, below the chart */}
        <Tabs defaultValue="bva" className="w-full" data-testid="budget-results-tabs">
          <TabsList>
            <TabsTrigger value="bva" data-testid="tab-budget-vs-actual">Budget vs Actual</TabsTrigger>
            <TabsTrigger value="forecast" data-testid="tab-by-property">By property (forecast)</TabsTrigger>
          </TabsList>

          {/* Budget vs Actual — per property, rolls up to year, expand for months */}
          <TabsContent value="bva">
            <div className="border rounded-lg bg-card p-4" data-testid="budget-vs-actual">
              {projLoading && !bva ? (
                <div className="flex flex-col items-center justify-center gap-3 py-8" data-testid="bva-loading">
                  <div className="animate-spin h-6 w-6 rounded-full border-2 border-sky-500 border-t-transparent" />
                  <span className="text-sm text-muted-foreground">Loading budget vs actual…</span>
                </div>
              ) : !bva || !(bva.by_property?.length) ? (
                <p className="text-sm text-muted-foreground py-6 text-center">
                  {bva?.message || 'No elapsed months yet — actuals appear once the budget period starts.'}
                </p>
              ) : (
                <>
                  <div className="flex items-center justify-between flex-wrap gap-2 mb-3">
                    <h2 className="text-sm font-semibold flex items-center gap-1.5">Budget vs Actual <span className="font-normal text-muted-foreground">by property · elapsed months</span>{CalcBasisInfo}</h2>
                    <div className="text-xs text-muted-foreground">
                      Portfolio: <span className="font-medium text-foreground">{money(bva.totals.actual)}</span> actual vs {money(bva.totals.budget)} budget
                      {bva.totals.variance_pct != null && (
                        <span className={bva.totals.variance > 0 ? 'text-destructive ml-1' : 'text-emerald-600 ml-1'}>
                          ({bva.totals.variance > 0 ? '+' : ''}{bva.totals.variance_pct}%)
                        </span>
                      )}
                    </div>
                  </div>
                  {/* header legend */}
                  <div className="hidden md:grid grid-cols-[1fr_repeat(var(--yrs),minmax(0,1fr))_minmax(0,1.1fr)] gap-2 px-3 pb-1.5 text-[11px] uppercase tracking-wider text-muted-foreground border-b"
                    style={{ '--yrs': (bva.years || []).length }}>
                    <span>Property</span>
                    {(bva.years || []).map((y) => <span key={y} className="text-right">{y}</span>)}
                    <span className="text-right">Total (act vs bud)</span>
                  </div>
                  <Accordion type="multiple" className="w-full">
                    {bva.by_property.slice(0, 80).map((p, pi) => (
                      <AccordionItem key={`${p.property_key}-${pi}`} value={`${p.property_key}-${pi}`} data-testid={`bva-prop-${p.property_key}`}>
                        <AccordionTrigger className="hover:no-underline py-2.5">
                          <div className="flex-1 grid md:grid-cols-[1fr_repeat(var(--yrs),minmax(0,1fr))_minmax(0,1.1fr)] grid-cols-[1fr_auto] gap-2 items-center text-sm pr-2"
                            style={{ '--yrs': (bva.years || []).length }}>
                            <span className="font-medium truncate text-left" title={p.property_name}>{p.property_name}</span>
                            {(bva.years || []).map((y) => (
                              <span key={y} className="hidden md:block text-right text-xs">
                                <span className="font-medium">{money(p.years[y]?.actual)}</span>
                                <span className="text-muted-foreground"> / {money(p.years[y]?.budget)}</span>
                              </span>
                            ))}
                            <span className="text-right text-xs">
                              <span className="font-semibold">{money(p.total.actual)}</span>
                              <span className="text-muted-foreground"> / {money(p.total.budget)}</span>
                              {p.total.variance_pct != null && (
                                <span className={p.total.variance > 0 ? 'text-destructive ml-1' : 'text-emerald-600 ml-1'}>
                                  {p.total.variance > 0 ? '+' : ''}{p.total.variance_pct}%
                                </span>
                              )}
                            </span>
                          </div>
                        </AccordionTrigger>
                        <AccordionContent>
                          <table className="w-full text-sm mb-2">
                            <thead>
                              <tr className="text-[11px] uppercase tracking-wider text-muted-foreground border-b">
                                <th className="text-left py-1.5 pl-3">Month</th>
                                <th className="text-right">Budget</th>
                                <th className="text-right">Actual</th>
                                <th className="text-right">Variance</th>
                                <th className="text-right pr-3">%</th>
                              </tr>
                            </thead>
                            <tbody>
                              {p.months.map((r) => {
                                const noData = !r.actual;
                                return (
                                <tr key={r.month} className="border-b last:border-0">
                                  <td className="py-1.5 pl-3">{r.month}</td>
                                  <td className="text-right">{money(r.budget)}</td>
                                  <td className="text-right">
                                    {noData
                                      ? <span className="text-muted-foreground italic text-xs">no bills posted</span>
                                      : money(r.actual)}
                                  </td>
                                  <td className={`text-right ${noData ? 'text-muted-foreground' : r.variance > 0 ? 'text-destructive' : 'text-emerald-600'}`}>{noData ? '—' : money(r.variance)}</td>
                                  <td className="text-right text-muted-foreground pr-3">{noData || r.variance_pct == null ? '—' : `${r.variance_pct}%`}</td>
                                </tr>
                                );
                              })}
                            </tbody>
                          </table>
                        </AccordionContent>
                      </AccordionItem>
                    ))}
                  </Accordion>
                  {bva.by_property.length > 80 && (
                    <p className="text-xs text-muted-foreground mt-2">Showing top 80 properties by budget.</p>
                  )}
                </>
              )}
            </div>
          </TabsContent>

          {/* By property (forecast) */}
          <TabsContent value="forecast">
            <div className="border rounded-lg bg-card p-4">
              <h2 className="text-sm font-semibold mb-3">Forecast by property × commodity <span className="font-normal text-muted-foreground">· full horizon (budget)</span></h2>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-xs uppercase tracking-wider text-muted-foreground border-b">
                      <th className="text-left py-2">Property</th>
                      {budget.granularity === 'meter' && <th className="text-left">Meter</th>}
                      <th className="text-left">State</th>
                      <th className="text-left">Commodity</th>
                      <th className="text-right">Supply</th>
                      <th className="text-right">Delivery</th>
                      <th className="text-right">Tax</th>
                      <th className="text-right">Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(projection?.by_scope || []).slice(0, 50).map((s, idx) => (
                      <tr key={`${s.scope_key}-${idx}`} className="border-b last:border-0" data-testid={`scope-row-${s.scope_key}`}>
                        <td className="py-1.5 max-w-[260px] truncate" title={s.property_name}>
                          <span className="inline-flex items-center gap-1.5">
                            {s.property_name}
                            {s.synthetic && <Badge className="bg-emerald-100 text-emerald-700 hover:bg-emerald-100 text-[10px]">New project</Badge>}
                          </span>
                        </td>
                        {budget.granularity === 'meter' && <td className="text-xs">{s.meter_number || '—'}</td>}
                        <td>{s.state || '—'}</td>
                        <td className="capitalize">{s.commodity}</td>
                        <td className="text-right">{money(s.supply)}</td>
                        <td className="text-right">{money(s.delivery)}</td>
                        <td className="text-right">{money(s.tax)}</td>
                        <td className="text-right font-medium">{money(s.total)}</td>
                      </tr>
                    ))}
                    {(projection?.by_scope || []).length === 0 && (
                      <tr><td colSpan={budget.granularity === 'meter' ? 8 : 7} className="py-6 text-center text-muted-foreground">No scopes in view.</td></tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </TabsContent>
        </Tabs>

        {/* New project (projected) properties */}
        <div className="border rounded-lg bg-card p-4" data-testid="projected-properties-card">
          <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
            <div className="flex items-center gap-2">
              <Building2 className="w-4 h-4 text-emerald-600" />
              <h2 className="text-sm font-semibold">New project properties</h2>
              <Badge variant="secondary">{(budget.projected_properties || []).length}</Badge>
            </div>
            <Button size="sm" variant="outline" onClick={() => setProjDialogOpen(true)}
              disabled={projSources.length === 0} data-testid="add-projected-btn">
              <Plus className="w-3.5 h-3.5 mr-1" /> Add new project
            </Button>
          </div>
          {(budget.projected_properties || []).length === 0 ? (
            <p className="text-sm text-muted-foreground">
              Model hypothetical new projects by cloning an existing property and scaling its
              meters — see combined 5-year spend for existing + new developments.
            </p>
          ) : (
            <div className="space-y-2">
              {(budget.projected_properties || []).map((p) => (
                <div key={p.id} className="flex items-center justify-between border rounded-md px-3 py-2"
                  data-testid={`projected-row-${p.id}`}>
                  <div className="min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-medium truncate">{p.name}</span>
                      <Badge className="bg-emerald-100 text-emerald-700 hover:bg-emerald-100 text-[10px]">New project</Badge>
                    </div>
                    <div className="text-xs text-muted-foreground">
                      cloned from {p.source_property_name} · {p.meter_multiplier}× meters
                      {p.commodities?.length ? ` · ${p.commodities.join(', ')}` : ''}
                    </div>
                  </div>
                  <button onClick={() => removeProjected(p)} className="px-1.5 text-muted-foreground hover:text-destructive"
                    title="Remove" data-testid={`projected-remove-${p.id}`}>
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      <ScenarioEditor
        open={editorOpen}
        onOpenChange={setEditorOpen}
        accountId={accountId}
        budgetId={budgetId}
        existing={editingScenario}
        commodities={blCommodities}
        onSaved={() => { loadBudget(); loadProjection(); }}
      />

      <Dialog open={!!expandedChart} onOpenChange={(o) => !o && setExpandedChart(null)}>
        <DialogContent className="max-w-5xl" data-testid="chart-expand-dialog">
          <DialogHeader>
            <DialogTitle>{expandedChart?.label} — projection</DialogTitle>
            <DialogDescription>Monthly cost breakdown, market overlay and actuals. Use the ⋯ menu to export PNG or CSV.</DialogDescription>
          </DialogHeader>
          {expandedChart && (
            <CommodityChart commodity={expandedChart.comm} title={expandedChart.label}
              data={expandedChart.data} bars={expandedChart.bars}
              hasMarket={expandedChart.hasMarket} feedLabel={expandedChart.feedLabel}
              marketLabel="At market" height={460} testid="chart-expanded" />
          )}
        </DialogContent>
      </Dialog>


      <RateModelingDialog
        open={rateDialogOpen}
        onOpenChange={setRateDialogOpen}
        accountId={accountId}
        budgetId={budgetId}
        scenario={activeScenario}
        budget={budget}
        onSaved={() => { loadBudget(); loadProjection(); }}
      />

      <AIPromptDialog
        open={aiDialogOpen}
        onOpenChange={setAiDialogOpen}
        accountId={accountId}
        budgetId={budgetId}
        onSaved={() => { loadBudget(); loadProjection(); }}
      />

      <ProjectedPropertyDialog
        open={projDialogOpen}
        onOpenChange={setProjDialogOpen}
        accountId={accountId}
        budgetId={budgetId}
        sources={projSources}
        onSaved={() => { loadBudget(); loadProjection(); }}
      />

      <ScenarioHistoryDialog
        open={!!historyScenario}
        onOpenChange={(v) => { if (!v) setHistoryScenario(null); }}
        accountId={accountId}
        budgetId={budgetId}
        scenarioId={historyScenario?.id}
        scenarioName={historyScenario?.name}
        onRestored={() => { setHistoryScenario(null); loadBudget(); loadProjection(); }}
      />

      <YoyEscalationDialog
        open={yoyOpen}
        onOpenChange={setYoyOpen}
        accountId={accountId}
        budgetId={budgetId}
        horizonMonths={budget.horizon_months}
        onSaved={() => loadBudget()}
      />

      <BudgetCopilotPanel
        open={beaconOpen}
        onOpenChange={setBeaconOpen}
        accountId={accountId}
        budgetId={budgetId}
        onChanged={() => { loadBudget(); loadProjection(); }}
      />
    </MainLayout>
  );
}
